/**
 * This module combines environment variables with known information about
 * subsystems in order to resolve various URLs for the subsystems.
 */
import { invariant } from '../util/invariant';
import { getServiceDocumentUrl } from './serviceDocRequest';
import type { ServiceLinksBase, SubsystemId} from './subsystemData';
import { subsystems } from './subsystemData';
import { subsystemEnvName } from './subsystems';

export function resolveLinks(env: NodeJS.ProcessEnv, defaultUrlRoot: string): ServiceLinksBase {
  const links: Record<string, string> = {};

  // When we want to call endpoints that run locally from a docker container,
  // on Windows and MacOS we can use the host name 'host.docker.internal' to
  // reach the host machine. This way, it is possible from a docker container
  // to query the Mimir service document using the url 'http://host.docker.internal:3006/api/v1'
  // and we will get back template links using 'http://host.docker.internal:*/' instead
  // of 'http://localhost:*/'. Since other stacks may be running locally as well on different
  // ports, let's rewrite all localhost urls with the docker host alias when the service
  // document is queried at a url that uses the docker host alias.
  const DOCKERHOST_PREFIX = 'http://host.docker.internal:';
  const LOCALHOST_PREFIX = 'http://localhost:';

  for (const [name, subsystem] of Object.entries(subsystems)) {
    const routes = subsystem.routes;
    if (routes == null) {
      continue;
    }

    const varName = subsystemEnvName(name as SubsystemId);
    let urlRoot = env[varName] ?? defaultUrlRoot;
    if (defaultUrlRoot.startsWith(DOCKERHOST_PREFIX) && urlRoot.startsWith(LOCALHOST_PREFIX)) {
      urlRoot = DOCKERHOST_PREFIX + urlRoot.substring(LOCALHOST_PREFIX.length);
    }


    for (const [linkName, pattern] of Object.entries(routes)) {
      links[linkName as keyof ServiceLinksBase] = urlRoot + pattern;
    }
  }
  return links as ServiceLinksBase;
}

type EnvRecord = Record<string, string | undefined>;

export function resolveSubsystemUrl(env: EnvRecord, subsystem: SubsystemId, fallback: string | null): string {
  const envName = subsystemEnvName(subsystem);
  const result = env[envName];
  if (result == null && fallback != null) {
    return fallback;
  }
  invariant(result, 'resolveSubsystemUrl missing ' + envName);
  return result;
}

export function resolveServiceDocumentRootUrl(env: EnvRecord): string {
  return resolveSubsystemUrl(env, 'prime', env['VUE_APP_AWS_API_PREFIX'] ?? env['MIMIR_HOME_URL'] ?? '');
}

/**
 * Returns the service document url. If the lambdas are deployed locally,
 * the local service document url is used; otherwise we return the regular
 * cloud url.
 *
 * The preferCloudServiceDocumentUrl parameter allows to obtain the cloud url
 * even when the lambdas are running locally. This is useful when lambdas
 * can initiate fargate operations that are run in the cloud even if the tasks
 * are triggered locally, since actual fargate tasks cannot use a local service
 * document url.
 */
export function resolveServiceDocumentUrl(env: EnvRecord, preferCloudServiceDocumentUrl = false): string {
  if (preferCloudServiceDocumentUrl && env.CLOUD_SERVICE_DOCUMENT_URL) {
    return env.CLOUD_SERVICE_DOCUMENT_URL;
  }
  const rootUrl = resolveServiceDocumentRootUrl(env);
  return getServiceDocumentUrl(rootUrl);
}
